export type Language = 'en' | 'sv'
export type ThemeMode = 'system' | 'light' | 'dark'

export interface CloudUserSettings {
  preferredLanguage: string
  themeMode: string
  checkedItemBehavior: string
  communityRecommendationOptOut: boolean
  onboardingCompleted: boolean
}

export interface CloudCategoryTranslation { language: string; name: string; source: string }
export interface CloudCategoryAlias { language: string; normalizedAlias: string; displayAlias: string }
export interface CloudCategory {
  id: string
  scope: string
  createdByUserId?: string | null
  createdAt: number
  updatedAt: number
  translations: CloudCategoryTranslation[]
  aliases: CloudCategoryAlias[]
}

export interface CloudStore {
  id: string
  name: string
  chain?: string | null
  city?: string | null
  address?: string | null
  countryCode?: string | null
  latitude?: number | null
  longitude?: number | null
  ownerUserId?: string | null
  createdAt: number
  updatedAt: number
}

export interface CloudLayoutSection {
  id: string
  categoryId: string
  displayOrder: number
  floorLabel?: string | null
  areaLabel?: string | null
  aisleLabel?: string | null
  createdAt: number
  updatedAt: number
  categoryNames: Record<string, string>
}

export interface CloudLayout {
  id: string
  storeId: string
  ownerUserId?: string | null
  name: string
  status: string
  validFrom?: number | null
  validTo?: number | null
  createdAt: number
  updatedAt: number
  sections: CloudLayoutSection[]
}

export interface CloudShoppingItem {
  id: string
  displayTitle: string
  originalInput: string
  productConceptId?: string | null
  quantityValue?: number | null
  quantityText?: string | null
  unit?: string | null
  note?: string | null
  isChecked: boolean
  checkedAt?: number | null
  categoryId?: string | null
  recommendationSource?: string | null
  recommendationConfidence?: string | null
  manualSortPosition: number
  createdAt: number
  updatedAt: number
  deletedAt?: number | null
}

export interface CloudShoppingList {
  id: string
  name: string
  storeId?: string | null
  layoutId?: string | null
  status: string
  createdAt: number
  updatedAt: number
  completedAt?: number | null
  items: CloudShoppingItem[]
  ownerUserId?: string
  memberRole?: string
}

export interface CloudAccountSnapshot {
  version: number
  settings?: CloudUserSettings | null
  categories: CloudCategory[]
  stores: CloudStore[]
  layouts: CloudLayout[]
  lists: CloudShoppingList[]
  archivedStoreIds: string[]
  archivedLayoutIds: string[]
  updatedAt: number
}

export interface ListInvite {
  id: string
  listId: string
  listName: string
  ownerUserId: string
  ownerEmail?: string | null
  recipientUserId: string
  recipientEmail: string
  status: string
  createdAt: number
  updatedAt?: number
}

export const defaultSettings = (language: Language = 'en'): CloudUserSettings => ({
  preferredLanguage: language,
  themeMode: 'system',
  checkedItemBehavior: 'keep_in_category',
  communityRecommendationOptOut: false,
  onboardingCompleted: true,
})

export const emptySnapshot = (): CloudAccountSnapshot => ({
  version: 1,
  categories: [],
  stores: [],
  layouts: [],
  lists: [],
  archivedStoreIds: [],
  archivedLayoutIds: [],
  updatedAt: Date.now(),
})
